import { useState } from 'react';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { RecordActions } from '../../components/RecordActions';
import { RECORD_FIELDS } from '../../components/recordFields';
import { KeyValue, Pill, Spinner, FormGrid, Field, TextInput, NumberInput, DateInput, TextArea, LineTable, SelectInput } from '../../components/ui';
import { SearchSelect } from '../../components/SearchSelect';
import { useDetail } from '../../lib/useDetail';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { dateLabel, num, workOrderStatus, serviceCycleStatus, physicalAssetStatus } from '../../lib/format';
import type { WorkOrder, WorkOrderOperation, WorkOrderMaterialSnapshot, StockMovement, Item, Bom, Contract, Warehouse, PhysicalAsset, AssetServiceCycle, ProcessTemplate, MaterialPlan } from '../../lib/types';

const today = () => new Date().toISOString().slice(0, 10);

// 核心服务多数写接口返回 { data: payload } 信封，api.post 不解包；个别历史写法可能直接返回 payload。
// 统一防御性解包，避免因信封约定差异导致运行时 undefined。
function unwrap<T>(body: unknown): T {
  if (body && typeof body === 'object' && 'data' in (body as Record<string, unknown>)) {
    return (body as { data: T }).data;
  }
  return body as T;
}

const QC_STATUS_META: Record<string, { tone: 'ok' | 'info' | 'warn' | 'bad' | 'neutral'; label: string }> = {
  not_required: { tone: 'neutral', label: '免检' },
  pending: { tone: 'warn', label: '待检验' },
  accepted: { tone: 'ok', label: '已合格' },
  rejected: { tone: 'bad', label: '不合格' },
  conditional: { tone: 'info', label: '让步接收' },
};

function QcStatusCell({ o }: { o: WorkOrderOperation }) {
  if (!o.qc_required) return <span className="muted">—</span>;
  const meta = QC_STATUS_META[o.qc_status ?? 'pending'] ?? QC_STATUS_META.pending;
  return <Pill tone={meta.tone} label={meta.label} />;
}

function WoOpsTable({ ops }: { ops: WorkOrderOperation[] }) {
  return (
    <LineTable<WorkOrderOperation>
      rows={ops}
      emptyText="该工单未展开工序任务（既未选工艺模板/BOM，或后续由 QC 回传补录）"
      columns={[
        { header: '工序', render: (o) => `${o.operation_code ?? ''} ${o.operation_name ?? ''}`.trim() || '—' },
        { header: '工位', render: (o) => o.workstation || '—' },
        { header: '计划工时(分)', align: 'right', render: (o) => <span className="num">{num(o.planned_minutes, 0)}</span> },
        { header: '质检', render: (o) => <QcStatusCell o={o} /> },
      ]}
    />
  );
}

  /**
   * 作废工单。
   *
   * 作废不是删除：记录保留，只是退出生产队列 / QC 任务 / 业务统计。
   * 后端会逐条校验「没有任何业务事实」（产出、领料、库存流水、质检、交货、返工、成本），
   * 不满足时返回结构化阻断清单，这里逐条显示 —— 免得管理员只看到一句「作废失败」，
   * 不知道该先退料还是先撤质检。
   *
   * 原因必填：没有原因就无法追责，作废就退化成另一种形式的删除。
   */
  function VoidPanel({ wo, onCancel, onDone }: { wo: WorkOrder; onCancel: () => void; onDone: () => void }) {
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [blockers, setBlockers] = useState<string[]>([]);

    const submit = async () => {
      setErr(null); setBlockers([]);
      if (!reason.trim()) { setErr('请填写作废原因（作废是终态，不可撤销）'); return; }
      setBusy(true);
      try {
        await api.post(`/api/work-orders/${wo.id}/void`, { reason: reason.trim() });
        onDone();
      } catch (e) {
        const details = e instanceof ApiError ? (e as ApiError & { details?: { blockers?: string[] } }).details : undefined;
        setBlockers(details?.blockers ?? []);
        setErr(e instanceof ApiError ? e.message : '作废失败');
      } finally { setBusy(false); }
    };

    return (
      <div className="notice warn" style={{ display: 'block' }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>作废工单 {wo.number}</div>
        <div style={{ marginBottom: 8 }}>
          作废是<b>终态</b>，不可撤销：工单将退出生产队列、QC 任务与业务统计，但记录保留可追责。
          仅当该工单<b>没有任何业务事实</b>时允许作废（无产出、无领料、无库存流水、无质检/交货/返工/成本）。
          如需继续生产，请新建工单。
        </div>
        <FormGrid>
          <Field label="作废原因" required>
            <TextArea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="如：前置 BOM 未批准时误建工单，无任何生产事实，作废重开"
            />
          </Field>
        </FormGrid>
        {err ? <div className="notice bad" style={{ marginTop: 8 }}>{err}</div> : null}
        {blockers.length ? (
          <ul style={{ margin: '8px 0 0 16px' }}>
            {blockers.map((b) => <li key={b}>{b}</li>)}
          </ul>
        ) : null}
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button className="btn" onClick={submit} disabled={busy || !reason.trim()}>{busy ? '处理中…' : '确认作废'}</button>
          <button className="btn ghost" onClick={onCancel} disabled={busy}>取消</button>
        </div>
      </div>
    );
  }

  function IssuePanel({ wo, onDone }: { wo: WorkOrder; onDone: () => void }) {
  const remaining = Number(wo.quantity) - Number(wo.issued_for_quantity);
  const [quantity, setQuantity] = useState(String(remaining > 0 ? remaining : 0));
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [warehouseLabel, setWarehouseLabel] = useState('');
  const [issueDate, setIssueDate] = useState(today());
  const [batch, setBatch] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const submit = async () => {
    setErr(null); setOk(null);
    if (!warehouseId) { setErr('请选择原材料仓库'); return; }
    if (!(Number(quantity) > 0)) { setErr('本次领料对应生产数量需大于 0'); return; }
    setBusy(true);
    try {
      const r = unwrap<{ work_order: WorkOrder; stock_movement: StockMovement }>(await api.post(`/api/work-orders/${wo.id}/issue-materials`, {
        production_quantity: Number(quantity), raw_warehouse_id: warehouseId, issue_date: issueDate, notes: notes || null,
        batch_number: batch || null,
      }));
      setOk(`已生成生产领料流水 ${r.stock_movement.number}，工单状态：${workOrderStatus(r.work_order.status).label}`);
      setTimeout(onDone, 700);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '领料失败');
    } finally { setBusy(false); }
  };

  return (
    <div className="notice info" style={{ display: 'block' }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>生产领料（按 BOM 用量 × 本次数量比例，从原材料仓扣减）</div>
      {err ? <div className="notice bad" style={{ marginBottom: 8 }}>{err}</div> : null}
      {ok ? <div className="notice ok" style={{ marginBottom: 8 }}>{ok}</div> : null}
      <FormGrid min={160}>
        <Field label={`本次数量（未领 ${num(remaining, 3)}）`} required><NumberInput value={quantity} onChange={(e) => setQuantity(e.target.value)} /></Field>
        <Field label="原材料仓库" required>
          <SearchSelect<Warehouse> endpoint="/api/warehouses" value={warehouseId} label={warehouseLabel}
            onChange={(row) => { setWarehouseId(row?.id ?? null); setWarehouseLabel(row ? `${row.code} ${row.name_zh}` : ''); }}
            optionTitle={(w) => `${w.code} · ${w.name_zh}`} placeholder="检索仓库…" />
        </Field>
        <Field label="领料日期" required><DateInput value={issueDate} onChange={(e) => setIssueDate(e.target.value)} /></Field>
        <Field label="批次号（选填，用于粉末批次追溯）"><TextInput value={batch} onChange={(e) => setBatch(e.target.value)} placeholder="仅批次强制的工单/合同必填" /></Field>
        <Field label="备注"><TextInput value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
      </FormGrid>
      <div className="detail-actions"><button className="btn primary" onClick={submit} disabled={busy}>{busy ? '过账中…' : '确认领料过账'}</button></div>
    </div>
  );
}

function ReturnPanel({ wo, onDone }: { wo: WorkOrder; onDone: () => void }) {
  const returnable = Number(wo.issued_for_quantity) - Number(wo.produced_quantity);
  const [quantity, setQuantity] = useState(String(returnable > 0 ? returnable : 0));
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [warehouseLabel, setWarehouseLabel] = useState('');
  const [returnDate, setReturnDate] = useState(today());
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const submit = async () => {
    setErr(null); setOk(null);
    if (!warehouseId) { setErr('请选择退回的原材料仓库'); return; }
    if (!(Number(quantity) > 0)) { setErr('退料对应生产数量需大于 0'); return; }
    setBusy(true);
    try {
      const r = unwrap<{ work_order: WorkOrder; stock_movement: StockMovement }>(await api.post(`/api/work-orders/${wo.id}/return-materials`, {
        production_quantity: Number(quantity), raw_warehouse_id: warehouseId, return_date: returnDate, notes: notes || null,
      }));
      setOk(`已生成退料入库流水 ${r.stock_movement.number}，工单状态：${workOrderStatus(r.work_order.status).label}`);
      setTimeout(onDone, 700);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '退料失败');
    } finally { setBusy(false); }
  };

  return (
    <div className="notice info" style={{ display: 'block' }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>退回未使用原材料（按 BOM 用量 × 本次数量比例，回入原材料仓并回退已领数量）</div>
      {err ? <div className="notice bad" style={{ marginBottom: 8 }}>{err}</div> : null}
      {ok ? <div className="notice ok" style={{ marginBottom: 8 }}>{ok}</div> : null}
      <FormGrid min={160}>
        <Field label={`本次数量（可退 ${num(returnable, 3)}）`} required><NumberInput value={quantity} onChange={(e) => setQuantity(e.target.value)} /></Field>
        <Field label="退回原材料仓库" required>
          <SearchSelect<Warehouse> endpoint="/api/warehouses" value={warehouseId} label={warehouseLabel}
            onChange={(row) => { setWarehouseId(row?.id ?? null); setWarehouseLabel(row ? `${row.code} ${row.name_zh}` : ''); }}
            optionTitle={(w) => `${w.code} · ${w.name_zh}`} placeholder="检索仓库…" />
        </Field>
        <Field label="退料日期" required><DateInput value={returnDate} onChange={(e) => setReturnDate(e.target.value)} /></Field>
        <Field label="备注"><TextInput value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
      </FormGrid>
      <div className="detail-actions"><button className="btn primary" onClick={submit} disabled={busy}>{busy ? '过账中…' : '确认退料过账'}</button></div>
    </div>
  );
}

function CompletePanel({ wo, onDone }: { wo: WorkOrder; onDone: () => void }) {
  const toProduce = Number(wo.quantity) - Number(wo.produced_quantity);
  const [quantity, setQuantity] = useState(String(toProduce > 0 ? toProduce : 0));
  const [productionDate, setProductionDate] = useState(today());
  const [uom, setUom] = useState('Unidad');
  const [batch, setBatch] = useState('');
  const [serials, setSerials] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const submit = async () => {
    setErr(null); setOk(null);
    if (!(Number(quantity) > 0)) { setErr('完工数量需大于 0'); return; }
    setBusy(true);
    try {
      const serialList = serials.split('\n').map((s) => s.trim()).filter(Boolean);
      const res = unwrap<{ work_order: WorkOrder; stock_movement: StockMovement | null; path?: string }>(await api.post(`/api/work-orders/${wo.id}/complete`, {
        quantity: Number(quantity), production_date: productionDate, uom: uom || 'Unidad',
        batch_number: batch || null, serial_numbers: serialList.length ? serialList : undefined,
      }));
      // 维修完工不产生成品入库（stock_movement=null），不能无条件读 number，否则会误报失败并诱发重复提交。
      const isRepairDone = res.path === 'repair_no_stock_in' || !res.stock_movement;
      setOk(isRepairDone
        ? `维修完工已记录（不产生成品入库），工单状态：${workOrderStatus(res.work_order.status).label}`
        : `完工入库流水 ${res.stock_movement!.number} 已生成，工单状态：${workOrderStatus(res.work_order.status).label}`);
      setTimeout(onDone, 900);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '完工失败');
    } finally { setBusy(false); }
  };

  return (
    <div className="notice info" style={{ display: 'block' }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>完工（制造：入成品仓；维修：只记完工数量，不产生成品库存）</div>
      {err ? <div className="notice bad" style={{ marginBottom: 8 }}>{err}</div> : null}
      {ok ? <div className="notice ok" style={{ marginBottom: 8 }}>{ok}</div> : null}
      <FormGrid min={160}>
        <Field label={`完工数量（待完工 ${num(toProduce, 3)}）`} required><NumberInput value={quantity} onChange={(e) => setQuantity(e.target.value)} /></Field>
        <Field label="完工日期" required><DateInput value={productionDate} onChange={(e) => setProductionDate(e.target.value)} /></Field>
        <Field label="单位"><TextInput value={uom} onChange={(e) => setUom(e.target.value)} /></Field>
        <Field label="批次号"><TextInput value={batch} onChange={(e) => setBatch(e.target.value)} /></Field>
        <Field label="序列号（每行一个，选填）" span><TextArea value={serials} onChange={(e) => setSerials(e.target.value)} placeholder="S/N …" /></Field>
      </FormGrid>
      <div className="detail-actions"><button className="btn primary" onClick={submit} disabled={busy}>{busy ? '过账中…' : '确认完工'}</button></div>
    </div>
  );
}

function ServiceCycleBindPanel({ wo, onDone }: { wo: WorkOrder; onDone: () => void }) {
  const [assetId, setAssetId] = useState<string | null>(null);
  const [assetLabel, setAssetLabel] = useState('');
  const [cycles, setCycles] = useState<AssetServiceCycle[]>([]);
  const [selectedCycleId, setSelectedCycleId] = useState<string>(wo.service_cycle_id ?? '');
  const [loadingCycles, setLoadingCycles] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const pickAsset = async (id: string | null, label: string) => {
    setAssetId(id); setAssetLabel(label); setCycles([]); setSelectedCycleId('');
    if (!id) return;
    setLoadingCycles(true);
    try {
      const detail = await api.get<PhysicalAsset>(`/api/physical-assets/${id}`);
      const cs = detail.service_cycles ?? [];
      setCycles(cs);
      // 默认选中当前版本（列表按 version DESC 返回，首行即最新版本）。
      if (cs.length) setSelectedCycleId(cs[0].id);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '加载维修版本失败');
    } finally { setLoadingCycles(false); }
  };

  const bind = async () => {
    setErr(null); setOk(null);
    if (!selectedCycleId) { setErr('请选择要绑定的维修版本'); return; }
    setBusy(true);
    try {
      const res = unwrap<WorkOrder>(await api.post(`/api/work-orders/${wo.id}/service-cycle`, { service_cycle_id: selectedCycleId }));
      const sc = res.service_cycle;
      setOk(`已绑定工件 ${sc?.customer_serial ?? ''} · V${sc?.version ?? ''}（仅建立关联，不改版本状态）`);
      setTimeout(onDone, 700);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '绑定失败');
    } finally { setBusy(false); }
  };

  const unbind = async () => {
    setErr(null); setOk(null);
    setBusy(true);
    try {
      await api.post<WorkOrder>(`/api/work-orders/${wo.id}/service-cycle`, { service_cycle_id: null });
      setOk('已解绑维修版本，后续领料/完工不再落工件关联位');
      setTimeout(onDone, 700);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '解绑失败');
    } finally { setBusy(false); }
  };

  return (
    <div className="notice info" style={{ display: 'block' }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>绑定维修版本（返厂维修/加工工单，把领料·完工·检验·交货串到具体工件版本）</div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>仅写入工单的关联位，绝不改版本状态（状态推进由「工件履历」页人工单一操作）；自有库存生产无需绑定，留空即隔离追溯。</div>
      {err ? <div className="notice bad" style={{ marginBottom: 8 }}>{err}</div> : null}
      {ok ? <div className="notice ok" style={{ marginBottom: 8 }}>{ok}</div> : null}
      <FormGrid min={180}>
        <Field label="客户工件（序列号）" span>
          <SearchSelect<PhysicalAsset> endpoint="/api/physical-assets" value={assetId} label={assetLabel}
            onChange={(row) => pickAsset(row?.id ?? null, row ? `${row.customer_serial}${row.asset_type ? ' · ' + row.asset_type : ''}` : '')}
            optionTitle={(a) => `${a.customer_serial}${a.asset_type ? ' · ' + a.asset_type : ''}`}
            optionMeta={(a) => `V${a.current_version} · ${physicalAssetStatus(a.status).label}`}
            placeholder="检索客户工件…" paginated />
        </Field>
        <Field label="维修版本" span>
          {loadingCycles ? <Spinner label="载入版本…" /> : (
            <SelectInput value={selectedCycleId} onChange={(e) => setSelectedCycleId(e.target.value)} disabled={!cycles.length}>
              <option value="">{cycles.length ? '请选择版本…' : '请先选择工件'}</option>
              {cycles.map((c) => <option key={c.id} value={c.id}>{`V${c.version} · ${serviceCycleStatus(c.status).label}${c.work_order_number ? ' · ' + c.work_order_number : ''}`}</option>)}
            </SelectInput>
          )}
        </Field>
      </FormGrid>
      <div className="detail-actions">
        <button className="btn primary" onClick={bind} disabled={busy || !selectedCycleId}>{busy ? '提交中…' : '确认绑定'}</button>
        {wo.service_cycle_id ? <button className="btn" onClick={unbind} disabled={busy}>解绑当前版本</button> : null}
      </div>
    </div>
  );
}

function WorkOrderDetail({ row, onChanged }: { row: WorkOrder; onChanged: () => void }) {
  const { user } = useAuth();
  const canWrite = can(user, 'work-orders', 'create');
  const { data, loading, error, reload } = useDetail<WorkOrder>(`/api/work-orders/${row.id}`);
  const [panel, setPanel] = useState<'none' | 'issue' | 'return' | 'complete' | 'bind' | 'void'>('none');
  // 作废是终态且必须留痕，后端只放行 admin；这里同步隐藏入口，避免给非管理员一个必然 403 的按钮。
  const isAdmin = can(user, 'work-orders', 'cancel');
  const wo = data ?? row;
  const done = () => { setPanel('none'); reload(); onChanged(); };
  const active = wo.status === 'released' || wo.status === 'in_progress';
  // 领料/退料现同时支持「装配式 BOM」与「工艺方案材料快照」两条来源；有任一即可过账。
  const hasMaterials = !!wo.bom_id || (wo.material_snapshots?.length ?? 0) > 0;
  const snapshots = wo.material_snapshots ?? [];

  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{wo.number}</span>
        <h3>{`${wo.item_code ?? ''} ${wo.item_name_zh ?? ''}`.trim() || '生产工单'}</h3>
        <Pill tone={workOrderStatus(wo.status).tone} label={workOrderStatus(wo.status).label} />
        <div className="spacer" />
        {loading ? <Spinner label="刷新…" /> : null}
      </div>
      {error ? <div className="notice bad">{error}</div> : null}
      <div className="kv-grid">
        <KeyValue k="生产物料">{`${wo.item_code ?? ''} ${wo.item_name_zh ?? ''}`.trim() || wo.item_id}</KeyValue>
        <KeyValue k="来源合同">{wo.contract_number || '—'}</KeyValue>
        <KeyValue k="生产依据">{wo.bom_number ? `BOM ${wo.bom_number}` : (wo.process_template_number ? `工艺 ${wo.process_template_number}` : '—')}</KeyValue>
        <KeyValue k="工艺模板">{wo.process_template_number || (wo.bom_number ? '（BOM 工序）' : '—')}</KeyValue>
        <KeyValue k="材料方案">{wo.material_plan_code || (wo.bom_number ? '（BOM 材料）' : '—')}</KeyValue>
        <KeyValue k="关联 BOM">{wo.bom_number || '—'}</KeyValue>
        <KeyValue k="工单数量">{num(wo.quantity, 3)}</KeyValue>
        <KeyValue k="已领料对应">{num(wo.issued_for_quantity, 3)}</KeyValue>
        <KeyValue k="已完工">{num(wo.produced_quantity, 3)}</KeyValue>
        <KeyValue k="计划开始">{dateLabel(wo.planned_start_date)}</KeyValue>
        <KeyValue k="计划完成">{dateLabel(wo.planned_end_date)}</KeyValue>
        <KeyValue k="维修版本绑定">
          {wo.service_cycle
            ? <span>{`${wo.service_cycle.customer_serial} · V${wo.service_cycle.version}`} <Pill tone={serviceCycleStatus(wo.service_cycle.status).tone} label={serviceCycleStatus(wo.service_cycle.status).label} /></span>
            : <span className="muted">自有库存（未绑定）</span>}
        </KeyValue>
        {wo.status === 'voided' ? (
          <KeyValue k="作废留痕">
            <span className="muted">{`${dateLabel(wo.voided_at)} · ${wo.void_reason || '未记录原因'}`}</span>
          </KeyValue>
        ) : null}
      </div>
      {wo.status === 'voided' ? (
        <div className="notice" style={{ margin: '8px 0' }}>
          该工单已作废：不再进入生产队列、QC 任务与业务统计，但记录保留可追责。如需继续生产请新建工单。
        </div>
      ) : null}
      {wo.status !== 'voided' && wo.status !== 'completed' && isAdmin ? (
        <div style={{ margin: '8px 0' }}>
          {panel === 'void' ? (
            <VoidPanel wo={wo} onCancel={() => setPanel('none')} onDone={done} />
          ) : (
            <button className="btn ghost" onClick={() => { setPanel('void'); }}>作废工单</button>
          )}
        </div>
      ) : null}
      <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', margin: '10px 0 4px' }}>工序任务（只记工时；工时计费待独立财务系统按合计工时核算）</div>
      <WoOpsTable ops={wo.operations ?? []} />
      {snapshots.length ? (
        <>
          <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', margin: '10px 0 4px' }}>
            材料快照（只记合金粉末等受控耗料；机加辅料不建账，不计损耗率）
          </div>
          <LineTable<WorkOrderMaterialSnapshot>
            rows={snapshots}
            columns={[
              { header: '用料物料', render: (s) => `${s.item_code ?? ''} ${s.item_name_zh ?? ''}`.trim() || s.item_id },
              { header: '单方用量', align: 'right', render: (s) => <span className="num">{num(s.quantity, 3)} {s.uom}</span> },
              { header: '损耗', render: () => <span className="muted">不计</span> },
              { header: '计划耗用', align: 'right', render: (s) => <span className="num">{num(s.planned_consumption, 3)} {s.uom}</span> },
              { header: '批次', render: (s) => (s.batch_required ? <Pill tone="warn" label="强制批次" /> : <span className="muted">可空</span>) },
            ]}
          />
        </>
      ) : (
        <div className="muted" style={{ fontSize: 12, margin: '8px 0' }}>
          无受控耗料（机加/研磨不记砂轮等辅料；熔覆仅记合金粉末）。
        </div>
      )}
      {canWrite && active ? (
        <div className="detail-actions" style={{ marginTop: 10 }}>
          {hasMaterials ? <button className="btn" onClick={() => setPanel((p) => (p === 'issue' ? 'none' : 'issue'))}>{panel === 'issue' ? '收起领料' : '领料'}</button> : null}
          {hasMaterials && Number(wo.issued_for_quantity) - Number(wo.produced_quantity) > 1e-9 ? <button className="btn" onClick={() => setPanel((p) => (p === 'return' ? 'none' : 'return'))}>{panel === 'return' ? '收起退料' : '退回原材料'}</button> : null}
          <button className="btn" onClick={() => setPanel((p) => (p === 'complete' ? 'none' : 'complete'))}>{panel === 'complete' ? '收起完工' : '完工入库'}</button>
          <button className="btn" onClick={() => setPanel((p) => (p === 'bind' ? 'none' : 'bind'))}>{panel === 'bind' ? '收起绑定' : '绑定维修版本'}</button>
        </div>
      ) : null}
      {panel === 'issue' && hasMaterials ? <IssuePanel wo={wo} onDone={done} /> : null}
      {panel === 'return' && hasMaterials ? <ReturnPanel wo={wo} onDone={done} /> : null}
      {panel === 'complete' ? <CompletePanel wo={wo} onDone={done} /> : null}
      {panel === 'bind' ? <ServiceCycleBindPanel wo={wo} onDone={done} /> : null}
    </div>
  );
}

function NewWorkOrderForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const [itemId, setItemId] = useState<string | null>(null);
  const [itemLabel, setItemLabel] = useState('');
  const [bomId, setBomId] = useState<string | null>(null);
  const [bomLabel, setBomLabel] = useState('');
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [templateLabel, setTemplateLabel] = useState('');
  const [planId, setPlanId] = useState<string | null>(null);
  const [planLabel, setPlanLabel] = useState('');
  const [contractId, setContractId] = useState<string | null>(null);
  const [contractLabel, setContractLabel] = useState('');
  const [finishedId, setFinishedId] = useState<string | null>(null);
  const [finishedLabel, setFinishedLabel] = useState('');
  const [wipId, setWipId] = useState<string | null>(null);
  const [wipLabel, setWipLabel] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [businessType, setBusinessType] = useState<'repair' | 'manufacturing'>('repair');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    if (!itemId) { setErr('请选择生产物料'); return; }
    if (!finishedId) { setErr('请选择成品仓库'); return; }
    if (!(Number(quantity) > 0)) { setErr('生产数量需大于 0'); return; }
    setBusy(true);
    try {
      await api.post('/api/work-orders', {
        item_id: itemId, quantity: Number(quantity), business_type: businessType,
        bom_id: bomId || null, contract_id: contractId || null,
        process_template_id: templateId || null, material_plan_id: planId || null,
        wip_warehouse_id: wipId || null, finished_warehouse_id: finishedId,
        planned_start_date: start || null, planned_end_date: end || null,
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '创建失败');
    } finally { setBusy(false); }
  };

  return (
    <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
      <div className="panel-head"><div><h2>新建生产工单</h2><div className="desc">写入核心服务 /api/work-orders · 工序按【已批准】工艺模板/BOM 展开（只记工时）· 材料只记合金粉末等受控耗料（机加辅料不建账、不计损耗率）</div></div></div>
      <div className="panel-body">
        {err ? <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div> : null}
        <FormGrid>
          <Field label="业务类型" required>
            <SelectInput value={businessType} onChange={(e) => setBusinessType(e.target.value as 'repair' | 'manufacturing')}>
              <option value="repair">客户工件维修（激光熔覆）</option>
              <option value="manufacturing">自有原材料制造</option>
            </SelectInput>
          </Field>
          <Field label="生产物料" required>
            <SearchSelect<Item> endpoint="/api/items" value={itemId} label={itemLabel}
              onChange={(row) => { setItemId(row?.id ?? null); setItemLabel(row ? `${row.code} ${row.name_zh}` : ''); }}
              optionTitle={(it) => `${it.code} · ${it.name_zh}`} placeholder="检索生产物料…" />
          </Field>
          <Field label="生产数量" required><NumberInput value={quantity} onChange={(e) => setQuantity(e.target.value)} /></Field>
          <Field label="关联 BOM（选填，用于展开工序与领料）">
            <SearchSelect<Bom> endpoint="/api/boms" value={bomId} label={bomLabel}
              onChange={(row) => {
                setBomId(row?.id ?? null); setBomLabel(row ? row.number : '');
                if (row && !itemId) { setItemId(row.item_id); setItemLabel(`${row.item_code ?? ''} ${row.item_name_zh ?? ''}`.trim()); }
              }}
              optionTitle={(b) => `${b.number} · ${(b.item_code ?? '') + ' ' + (b.item_name_zh ?? '')}`.trim()} placeholder="检索 BOM…" />
          </Field>
          <Field label="工艺模板（选填·仅已批准，优先于 BOM 展开工序）">
            <SearchSelect<ProcessTemplate> endpoint="/api/process-templates" value={templateId} label={templateLabel}
              params={{ status: 'approved' }}
              onChange={(row) => { setTemplateId(row?.id ?? null); setTemplateLabel(row ? `${row.code} ${row.name_zh}` : ''); }}
              optionTitle={(t) => `${t.code} · ${t.name_zh}`}
              optionMeta={(t) => `V${t.version} · ${(t.operation_count ?? 0)} 道工序`} placeholder="检索已批准工艺模板…" />
          </Field>
          <Field label="材料方案（选填·仅已批准，冻结材料快照供领退料）">
            <SearchSelect<MaterialPlan> endpoint="/api/material-plans" value={planId} label={planLabel}
              params={{ status: 'approved' }}
              onChange={(row) => { setPlanId(row?.id ?? null); setPlanLabel(row ? `${row.code} ${row.name_zh}` : ''); }}
              optionTitle={(p) => `${p.code} · ${p.name_zh}`}
              optionMeta={(p) => `V${p.version} · ${(p.line_count ?? 0)} 项用料`} placeholder="检索已批准材料方案…" />
          </Field>
          <Field label="来源合同（选填，用于血缘追溯）">
            <SearchSelect<Contract> endpoint="/api/contracts" value={contractId} label={contractLabel}
              onChange={(row) => { setContractId(row?.id ?? null); setContractLabel(row ? row.number : ''); }}
              optionTitle={(c) => c.number} optionMeta={(c) => c.customer_name} placeholder="检索销售合同…" paginated />
          </Field>
          <Field label="成品仓库" required>
            <SearchSelect<Warehouse> endpoint="/api/warehouses" value={finishedId} label={finishedLabel}
              onChange={(row) => { setFinishedId(row?.id ?? null); setFinishedLabel(row ? `${row.code} ${row.name_zh}` : ''); }}
              optionTitle={(w) => `${w.code} · ${w.name_zh}`} placeholder="检索成品仓…" />
          </Field>
          <Field label="在制品仓（选填）">
            <SearchSelect<Warehouse> endpoint="/api/warehouses" value={wipId} label={wipLabel}
              onChange={(row) => { setWipId(row?.id ?? null); setWipLabel(row ? `${row.code} ${row.name_zh}` : ''); }}
              optionTitle={(w) => `${w.code} · ${w.name_zh}`} placeholder="检索在制品仓…" />
          </Field>
          <Field label="计划开始"><DateInput value={start} onChange={(e) => setStart(e.target.value)} /></Field>
          <Field label="计划完成"><DateInput value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
        </FormGrid>
        <div className="detail-actions">
          <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '提交中…' : '创建工单'}</button>
          <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
        </div>
      </div>
    </section>
  );
}

export function WorkOrdersPage() {
  const { user } = useAuth();
  const [reloadSignal, setReloadSignal] = useState(0);
  const [creating, setCreating] = useState(false);
  const canWrite = can(user, 'work-orders', 'create');

  const columns: Column<WorkOrder>[] = [
    { header: '工单号', render: (w) => <span className="num strong">{w.number}</span> },
    { header: '生产物料', render: (w) => `${w.item_code ?? ''} ${w.item_name_zh ?? ''}`.trim() || '—' },
    { header: '来源合同', render: (w) => <span className="muted">{w.contract_number || '—'}</span> },
    { header: '数量', align: 'right', render: (w) => <span className="num">{num(w.quantity, 3)}</span> },
    { header: '已领料', align: 'right', render: (w) => <span className="num">{num(w.issued_for_quantity, 3)}</span> },
    { header: '已完工', align: 'right', render: (w) => <span className="num">{num(w.produced_quantity, 3)}</span> },
    { header: '计划完成', render: (w) => <span className="muted">{dateLabel(w.planned_end_date)}</span> },
    { header: '状态', render: (w) => <Pill tone={workOrderStatus(w.status).tone} label={workOrderStatus(w.status).label} /> },
  ];

  return (
    <>
      {creating ? (
        <NewWorkOrderForm onCancel={() => setCreating(false)} onCreated={() => { setCreating(false); setReloadSignal((n) => n + 1); }} />
      ) : null}
      <ListPage<WorkOrder>
        title="生产工单"
        desc="工单主流程 · /api/work-orders。展开可查看工序任务并执行领料、退回原材料、完工入库（写入真实库存流水，状态自动流转）"
        endpoint="/api/work-orders"
        columns={columns}
        rowId={(w) => w.id}
        searchPlaceholder="搜索工单号 / 物料编码 / 名称 / 合同号…"
        reloadSignal={reloadSignal}
        rowActions={(row) => <RecordActions type="work-orders" row={row} fields={RECORD_FIELDS['work-orders']} label={String(row.number)} onDone={() => setReloadSignal((n) => n + 1)} />}
        actions={canWrite ? (
          <button className="btn primary" onClick={() => setCreating((v) => !v)}>{creating ? '收起新建' : '新建工单'}</button>
        ) : undefined}
        renderDetail={(row) => <WorkOrderDetail row={row} onChanged={() => setReloadSignal((n) => n + 1)} />}
      />
    </>
  );
}
